// External packages
import { useNavigate } from 'react-router-dom';
import * as React from 'react';

// Types
import type {
  LoginInput,
  AuthResponse,
} from '../../../server/src/types/auth.types';

// Utils
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'hooks/useToast';
import { API_BASE_URL } from './base';

export interface LoginResponse {
  error: boolean;
  message: string;
  data: AuthResponse;
}

export async function login({
  email,
  password,
}: LoginInput): Promise<LoginResponse> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/auth/login`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to login');
    }

    const result = await response.json();
    if (result.error) {
      throw new Error(result.message || 'Login failed');
    }

    return result;
  } catch (error: any) {
    throw new Error(error.message || 'Network error during login');
  }
}

export const useLogin = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      if (data.data.token) {
        localStorage.setItem('authToken', data.data.token);
        // Drop every cached query — the new session may belong to a
        // different organization, and stale cache from the previous login
        // would briefly (or persistently) show the wrong tenant's data.
        queryClient.clear();
        toast.success('Uspješno ste se prijavili!');
      }
    },
    onError: (error: any) => {
      console.error('Login failed:', error);
      toast.error(error?.message || 'Neuspješna prijava. Provjerite podatke.');
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    localStorage.removeItem('authToken');
    // Clear cached data so the next login (possibly another organization)
    // starts from a clean slate.
    queryClient.clear();
    toast.success('Uspješno ste se odjavili!');
    navigate('/login');
  }, [navigate, toast, queryClient]);
};
